import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button } from "antd";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import FlightHotelRoomSelectionPage from "../unifiedSearchMockup/FlightHotelRoomSelectionPage";
import FlightHotelPassengersPage from "../unifiedSearchMockup/FlightHotelPassengersPage";
import FlightHotelPaymentPage from "../unifiedSearchMockup/FlightHotelPaymentPage";
import "../packageProfileMockup/PackageProfileMockupPage.css";
import { mapHubwayzToFlightHotelCards } from "../unifiedSearchMockup/mapHubwayzToFlightHotelCards";
import { searchHubwayzHotels } from "../../services/hubwayzHotelsApi";
import { readHubwayzHotelsTabSnapshot } from "./searchHotelsViaHubwayz";
import { HUBWAYZ_HOTEL_ID_PREFIX } from "./hubwayzHotelsTabConfig";
import { hideSupplierBrandFromUserText } from "../unifiedSearchMockup/hideSupplierBrandFromUserText";
import { useAgentEntitlements } from "../agentEntitlements/useAgentEntitlements";
import {
  filterMishorResultsToHotel,
  hubwayzSearchFailure,
  resolveHubwayzHotelPageSearch,
  supplierStatusFromSearchResponse,
} from "./hubwayzHotelLiveSearch";

/**
 * Hotel page runs a fresh Hubwayz search for this hotel id.
 * The request currency and stay come from the search the user already ran.
 * Saved results are not shown in place of that search.
 */
export default function HubwayzHotelBookingPage() {
  const { hotelId: hotelIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlCurrency = searchParams.get("currency");
  const hotelId = String(hotelIdParam || "").replace(
    new RegExp(`^${HUBWAYZ_HOTEL_ID_PREFIX}`),
    ""
  );

  const { status: entitlementsStatus, canUseHotelPrebooking } = useAgentEntitlements();
  const deferredPaymentSystemAllowed =
    entitlementsStatus === "ok" && canUseHotelPrebooking === true;
  const [flowStep, setFlowStep] = useState("detail");
  const [bookingDraft, setBookingDraft] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [failure, setFailure] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!hotelId) return undefined;
    let cancelled = false;
    const pageSearch = resolveHubwayzHotelPageSearch({
      snapshot: readHubwayzHotelsTabSnapshot(),
      urlCurrency,
    });
    setPhase("loading");
    setFailure(null);
    setPayload(null);
    setFlowStep("detail");
    setBookingDraft(null);

    if (!pageSearch.ok) {
      setFailure({
        auth: false,
        status: "error",
        http: null,
        message:
          "לא נבחר מטבע לחיפוש. חזרו לחיפוש ובחרו מטבע — העמוד לא מחליף מטבע לבד.",
      });
      setPhase("error");
      return undefined;
    }

    const stay = pageSearch.stay;

    (async () => {
      try {
        const res = await searchHubwayzHotels({
          destinationId: hotelId,
          destinationType: "hotel",
          checkIn: stay.checkIn,
          checkOut: stay.checkOut,
          rooms: stay.rooms,
          customerCountry: "IL",
          currencies: pageSearch.currencies,
          waitUntilDone: true,
          enrichStatic: true,
        });
        if (cancelled) return;
        const supplierStatus = supplierStatusFromSearchResponse(res);
        if (!res?.ok || supplierStatus === "error" || res?.authError) {
          setPayload(null);
          setFailure({
            auth: res?.authError === true,
            status: "error",
            http: null,
            message: res?.authError
              ? "שגיאת אימות. לא מוצגות תוצאות שמורות או מזיכרון הדפדפן."
              : hideSupplierBrandFromUserText(res?.error, "החיפוש נכשל"),
          });
          setPhase("error");
          return;
        }
        const filtered = filterMishorResultsToHotel(res.data, hotelId);
        setPayload({
          res,
          filtered,
          stay,
          displayCurrency: pageSearch.displayCurrency,
        });
        setPhase("ready");
      } catch (err) {
        if (cancelled) return;
        setPayload(null);
        setFailure(hubwayzSearchFailure(err));
        setPhase("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hotelId, urlCurrency]);

  const liveContext = useMemo(() => {
    if (!payload?.filtered || !hotelId) return null;
    const { filtered, stay, res, displayCurrency } = payload;
    const rooms = Array.isArray(stay.rooms) ? stay.rooms : [{ adults: 2 }];
    const paxCount = rooms.reduce((sum, room) => {
      const adultsNum = Number(room?.adults);
      const adults = Number.isFinite(adultsNum) && adultsNum >= 1 ? adultsNum : 1;
      const ages = Array.isArray(room?.childAges) ? room.childAges.length : 0;
      return sum + adults + ages;
    }, 0);
    const cards = mapHubwayzToFlightHotelCards(filtered, {
      dateRangeLabel: `${stay.checkIn} → ${stay.checkOut}`,
      nights: null,
      paxCount: Math.max(1, paxCount),
      destinationId: hotelId,
      searchRequest: res.requestPayload,
      displayCurrency,
    });
    const resultsCard = cards.find((card) => String(card.hotelId) === String(hotelId));
    if (!resultsCard || !Array.isArray(filtered.results)) return null;
    return {
      resultsCard,
      flightOffers: [],
      hubwayzData: filtered,
      searchRequest: res.requestPayload,
      searchId: res.searchId || null,
      verifiedQuotes: Array.isArray(res.verifiedQuotes) ? res.verifiedQuotes : [],
      displayCurrency,
      appliedSearch: {
        checkIn: stay.checkIn,
        checkOut: stay.checkOut,
        rooms,
        currency: displayCurrency,
        dateRangeLabel: `${stay.checkIn} → ${stay.checkOut}`,
      },
    };
  }, [payload, hotelId]);

  const backToSearch = () => navigate("/p/dashboard/searchoffer");

  if (!hotelId) {
    return (
      <div dir="rtl" style={{ padding: 24 }}>
        <Alert type="warning" message="חסר מזהה מלון" showIcon />
        <Button type="primary" style={{ marginTop: 12 }} onClick={backToSearch}>
          חזרה לחיפוש
        </Button>
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div
        className="hpm-mock hpm-hotel-skel"
        dir="rtl"
        lang="he"
        data-testid="hubwayz-live-search-loading"
        aria-busy="true"
        aria-label="טוען את פרטי המלון"
      >
        <header className="hpm-topbar">
          <div className="hpm-topbar__brand">
            <img
              className="hpm-hotel-skel__logo"
              src={HOTELSRIVER_OFFICIAL_LOGO_URL}
              alt="HotelsRiver"
            />
          </div>
        </header>
        <div className="hpm-layout">
          <main className="hpm-main" aria-hidden="true">
            <section className="hpm-hero hpm-hotel-skel__hero">
              <span className="hpm-rooms-skel__line hpm-hotel-skel__title" />
              <span className="hpm-rooms-skel__line hpm-hotel-skel__meta" />
              <span className="hpm-hotel-skel__gallery" />
            </section>
            <div className="hpm-rooms-skel">
              {[0, 1, 2].map((i) => (
                <div key={`hubwayz-room-skel-${i}`} className="hpm-rooms-skel__row">
                  <div className="hpm-rooms-skel__main">
                    <span className="hpm-rooms-skel__radio" />
                    <div className="hpm-rooms-skel__lines">
                      <span className="hpm-rooms-skel__line hpm-rooms-skel__line--title" />
                      <span className="hpm-rooms-skel__line hpm-rooms-skel__line--meta" />
                    </div>
                  </div>
                  <div className="hpm-rooms-skel__price">
                    <span className="hpm-rooms-skel__line hpm-rooms-skel__line--price" />
                    <span className="hpm-rooms-skel__line hpm-rooms-skel__line--fx" />
                  </div>
                </div>
              ))}
            </div>
          </main>
          <aside className="hpm-summary" aria-hidden="true">
            <div className="hpm-summary__card hpm-hotel-skel__summary">
              <span className="hpm-rooms-skel__line hpm-hotel-skel__title" />
              <span className="hpm-rooms-skel__line hpm-hotel-skel__meta" />
              <span className="hpm-rooms-skel__line hpm-hotel-skel__price" />
            </div>
          </aside>
        </div>
      </div>
    );
  }

  if (phase === "error" || failure) {
    return (
      <div dir="rtl" style={{ padding: 24 }}>
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          showIcon
          message={failure?.auth ? "שגיאת אימות" : "החיפוש נכשל"}
          description={failure?.message || "החיפוש החי לא הושלם. לא מוצגות תוצאות שמורות."}
        />
        <Button type="primary" style={{ marginTop: 12 }} onClick={backToSearch}>
          חזרה לחיפוש מלונות
        </Button>
      </div>
    );
  }

  if (flowStep === "payment" && bookingDraft && liveContext) {
    return (
      <FlightHotelPaymentPage
        bookingDraft={bookingDraft}
        appliedSearch={liveContext.appliedSearch}
        hubwayzSearchRequest={liveContext.searchRequest}
        onBack={() => setFlowStep("passengers")}
        onStage={(id) => {
          if (id === "results") backToSearch();
          else if (id === "detail") setFlowStep("detail");
          else if (id === "passengers") setFlowStep("passengers");
        }}
        onDone={(meta) => {
          setBookingDraft(null);
          setFlowStep("detail");
          const hrId = meta?.hrOrderId;
          if (hrId) {
            navigate(`/p/dashboard/orders/hotels/${hrId}`);
            return;
          }
          const bid =
            meta?.bookingID ||
            meta?.bookingId ||
            meta?.hotelHold?.bookingId ||
            meta?.hrOrderNumber ||
            "";
          const q = bid
            ? `view=all&search=${encodeURIComponent(String(bid))}`
            : "view=all";
          navigate(`/p/dashboard/orders?${q}`);
        }}
      />
    );
  }

  if (flowStep === "passengers" && bookingDraft && liveContext) {
    return (
      <FlightHotelPassengersPage
        bookingDraft={bookingDraft}
        appliedSearch={liveContext.appliedSearch}
        onBack={() => setFlowStep("detail")}
        onResults={backToSearch}
        onExpiredSearch={() => {
          setBookingDraft(null);
          setFlowStep("detail");
          backToSearch();
        }}
        deferredPaymentSystemAllowed={deferredPaymentSystemAllowed}
        onContinueToPayment={(next) => {
          setBookingDraft(next);
          setFlowStep("payment");
        }}
      />
    );
  }

  return (
    <div>
      {liveContext ? (
        <FlightHotelRoomSelectionPage
          packageId={liveContext.resultsCard.id}
          liveContext={liveContext}
          onBack={backToSearch}
          onContinue={(draft) => {
            setBookingDraft(draft);
            setFlowStep("passengers");
          }}
        />
      ) : (
        <div dir="rtl" style={{ padding: 24 }}>
          <Alert
            type="warning"
            showIcon
            message="החיפוש החי הסתיים בלי הצעות למלון זה"
            description="לא מוצג כרטיס מלון סטטי ולא תוצאה שמורה מדפדפן."
          />
        </div>
      )}
    </div>
  );
}
